import { Component, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
	BusinessUser,
	BusinessUserRole,
	BusinessUserStatus,
} from '@models/businesses';
import { UserFunctionsService } from '@functions/user.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BusinessUsersService } from '@repos/businesses/users/business-users.service';
import { AuthState } from '@state/auth';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import LogRocket from 'logrocket';
import { take } from 'rxjs/operators';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { MatDialog } from '@angular/material/dialog';
import { InviteModalComponent } from '../invites/invite-modal/invite-modal.component';
import { ICON } from '@core/util/icon';
import { AppState } from '@state/app/app.state';
import {
	DeactivateUserModalComponent,
	DeactivateUserModalData,
} from '../deactivate-user-modal/deactivate-user-modal.component';
import { MatterService } from 'src/app/shared/api-repos/matter/matter.service';

interface UserFilter {
	search: string;
	role: string;
	status: BusinessUserStatus;
}

type DataSourceBusinessUser = BusinessUser & {
	fullName: string;
	fullNameLowercase: string;
	emailLowercase: string;
};

const roleSortValues = {
	[BusinessUserRole.LAWYER]: 1,
	[BusinessUserRole.CLERK]: 2,
	[BusinessUserRole.OTHER]: 3,
};

@UntilDestroy()
@Component({
	selector: 'app-current-users',
	templateUrl: './current-users.component.html',
	standalone: false,
})
export class CurrentUsersComponent implements OnInit {
	public dataSource = new MatTableDataSource([]);
	public icon = ICON;
	public roles = BusinessUserRole;

	public adminCount = 0;
	public statuses = BusinessUserStatus;
	public addingRemovingUser = null;
	public statusItems: RadioGroupItem<BusinessUserStatus>[] = [
		{ label: 'Active', value: BusinessUserStatus.ACTIVE },
		{ label: 'Deactivated', value: BusinessUserStatus.INACTIVE },
	];

	public roleFilterItems = ['all', ...Object.values(this.roles)];
	public users: BusinessUser[] = [];

	public userSearch = null;
	public filter: UserFilter = {
		search: null,
		role: 'all',
		status: BusinessUserStatus.ACTIVE,
	};

	@Select(BusinessUsersState.getUsers) users$: Observable<BusinessUser[]>;
	@ViewSelectSnapshot(AuthState.getUserId) userId: string;
	@SelectSnapshot(BusinessUsersState.getLoggedInBusinessUser)
	loggedInUser: BusinessUser;
	@SelectSnapshot(AppState.loadedFromLeap) loadedFromLeap: boolean;

	public displayedColumns: string[] = [
		'fullNameLowercase',
		'emailLowercase',
		'role',
		'restricted',
		'admin',
		'actions',
	];

	constructor(
		private snackBarService: SnackbarService,
		private businessUsersService: BusinessUsersService,
		private userFunctionService: UserFunctionsService,
		private dialog: MatDialog,
		private matterService: MatterService,
	) {}

	@ViewChild(MatSort)
	set sort(value: MatSort) {
		this.dataSource.sort = value;
	}

	ngOnInit(): void {
		this.users$.pipe(untilDestroyed(this)).subscribe((users) => {
			this.users = users;
			this.dataSource.data = this.getDataSourceFromBusinessUsers(users);
			this.dataSource.filterPredicate = this.tableFilter();
			this.applyFilter();
		});

		if (this.loadedFromLeap) {
			this.displayedColumns = [
				'fullNameLowercase',
				'emailLowercase',
				'role',
				'restricted',
				'admin',
			];
		}
	}

	async setRole(id: string, role: BusinessUserRole): Promise<void> {
		await this.businessUsersService.SetUserRole(id, role);
	}

	openSnackBar(message: string): void {
		this.snackBarService.openSnackBar({ text: message });
	}

	public changeRoleFilter(role: string): void {
		this.filter.role = role;
		this.applyFilter();
	}

	applyFilter = (): void => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		this.dataSource.filter = this.filter as any;
	};

	tableFilter(): (businessUser: BusinessUser, filter) => boolean {
		// Filter cannot be UserFilter here because mat-table requires the filter param to be of type any
		return (businessUser: BusinessUser, filter): boolean => {
			if (
				this.loggedInUser.restricted &&
				businessUser.id !== this.loggedInUser.id
			) {
				return false;
			}
			if (businessUser?.status?.trim() !== (filter as UserFilter).status) {
				return false;
			}
			if (
				businessUser?.role?.trim() !== (filter as UserFilter).role &&
				(filter as UserFilter).role !== 'all'
			) {
				return false;
			}

			const searchQuery =
				(filter as UserFilter)?.search?.trim().toLowerCase() ?? '';
			const searchTokens = searchQuery
				.split(' ')
				.filter((token: string) => token.trim().length);

			if (searchTokens.length) {
				const stringsToSearch = [
					businessUser?.firstName?.trim().toLowerCase(),
					businessUser?.lastName?.trim().toLowerCase(),
					businessUser?.email?.trim().toLowerCase(),
				].filter((chunk) => !!chunk);

				return searchTokens.every((token) =>
					stringsToSearch.some((word) => word.includes(token)),
				);
			}

			return true;
		};
	}

	// deleteUser = userId => {
	// 	this.openSnackBar('Coming soon! Feature not implemented yet.');
	// };

	setRestricted = async (
		userId: string,
		restricted: boolean,
		name: string,
	): Promise<void> => {
		try {
			await this.businessUsersService.SetRestricted(userId, restricted);
			this.snackBarService.openSnackBar({
				text: `Restrictive view ${restricted ? 'enabled' : 'disabled'} for ${name}.`,
				type: 'success',
			});
		} catch (err) {
			LogRocket.captureException(err);
			this.snackBarService.openSnackBar({
				text: 'Could not set restrictive permissions.',
				type: 'error',
			});
		}
	};

	setAdmin = async (
		userId: string,
		admin: boolean,
		name: string,
	): Promise<void> => {
		if (this.userId !== userId) {
			try {
				await this.businessUsersService.SetUserAdmin(userId, admin);
				this.snackBarService.openSnackBar({
					text: `Admin permissions ${admin ? 'enabled' : 'disabled'} for ${name}.`,
					type: 'success',
				});
			} catch (err) {
				LogRocket.captureException(err);
				this.snackBarService.openSnackBar({
					text: 'Could not set admin permissions.',
					type: 'error',
				});
			}
		}
	};

	openRemoveModal(user: BusinessUser): void {
		if (user.id !== this.userId) {
			this.matterService
				.GetUserAssignedActiveMatterCount(user.id, user.role)
				.subscribe(
					(count) => {
						this.dialog.open(DeactivateUserModalComponent, {
							data: {
								assignedMatterCount: count,
								currentUser: user,
								allUsers: this.users,
							} as DeactivateUserModalData,
							width: '50vw',
							maxWidth: '650px',
							panelClass: 'no-padding',
						});
					},
					(error) => {
						console.error(error);
						this.snackBarService.openSnackBar({
							text: 'Could not get user assigned matters count. Please try again.',
							type: 'error',
						});
					},
				);
		}
	}

	async reAdd(user: BusinessUser): Promise<void> {
		try {
			this.addingRemovingUser = user.id;
			await this.userFunctionService
				.makeUserActive(user.id)
				.pipe(take(1))
				.toPromise();
			this.snackBarService.openSnackBar({
				text: `${user.firstName} ${user.lastName} successfully activated`,
				type: 'success',
			});
		} catch (err) {
			LogRocket.captureException(err);
			LogRocket.log('Unable to make user active', user.id, err);
			this.snackBarService.openSnackBar({
				text: `There was an error when attempting to reactivate user ${user.firstName} ${user.lastName}`,
				type: 'error',
			});
		} finally {
			this.addingRemovingUser = null;
		}
	}

	public openInvitesModal(): void {
		this.dialog.open(InviteModalComponent);
	}

	trackById(index: number, item): string {
		return item.id;
	}

	/**
	 * Converts a BusinessUser[] to DataSourceBusinessUser[] required by the table and row sorting feature. Users are grouped by roles and sorted alphabetically by fullNames within their role group.
	 **/
	private getDataSourceFromBusinessUsers(
		businessUsers: BusinessUser[],
	): DataSourceBusinessUser[] {
		const dataSourceBusinessUsers = businessUsers
			?.map((user): DataSourceBusinessUser => {
				const fullName = `${user.firstName ?? 'Name unavailable'} ${user.lastName ?? 'Name unavailable'}`;
				return {
					...user,
					fullName,
					fullNameLowercase: fullName?.toLowerCase(),
					email: user.email || 'Not Added',
					emailLowercase: user.email?.toLowerCase() || '',
				};
			})
			.sort((a, b) => {
				if (a.role !== b.role) {
					return roleSortValues[a.role] - roleSortValues[b.role];
				}

				if (a.fullNameLowercase < b.fullNameLowercase) return -1;
				if (a.fullNameLowercase > b.fullNameLowercase) return 1;
				return 0;
			});

		return dataSourceBusinessUsers;
	}
}
